import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import '../../../styles/settingsNav.css';
import '../../../styles/teamsIntegration.css';
import { supabaseClient } from '../../../config/supabase';
import HoverHelp from '../../shared/HoverHelp';
import Button3D from '../../ui/Button3D';
import { useSaveToast } from '../../ui/SaveToast';
import { INTEGRATION_KEYS, canUseIntegration, useIntegrationAccess } from '../../../lib/integrations/integrationGate';
import {
    TEAM_ROLE_WORDS, addTeamMember, loadTeam, loadTeamSpaces, removeTeamMember, saveTeamSettings, setTeamRole, teamPermissions,
} from '../../../lib/integrations/teamSpaces';
import { PillGroup, SwitchRow } from './TeamsControls';

export const TEAM_SPACES_PATH = '/team-spaces';

const HELP = {
    title: 'Team settings',
    body: 'A Tenant Admin makes someone the Team Owner. The Team Owner then chooses the Team Admins, the members and these settings. Team Admins add and remove members as the owner allows. IntelAgent never changes Teams membership. Every change goes to the audit log.',
};

function TeamDetail({ team, orgId, client, onChanged }) {
    const toast = useSaveToast();
    const [detail, setDetail] = useState(null);
    const [draft, setDraft] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [pick, setPick] = useState('');

    const load = useCallback(async () => {
        try {
            const next = await loadTeam(client, team.team_id);
            setDetail(next); setDraft(next.settings); setError('');
        } catch (failure) { setError(failure?.message || 'This team could not be loaded.'); }
    }, [client, team.team_id]);
    useEffect(() => { load(); }, [load]);

    const run = async (work) => {
        setBusy(true); setError('');
        try { await work(); toast.saved(); await load(); onChanged(); } catch (failure) {
            const message = failure?.message || 'That change could not be saved.';
            setError(message); toast.failed(message);
        } finally { setBusy(false); }
    };

    if (!detail) return error ? <p role="alert" className="ia-teams__error">{error}</p> : <p>Loading…</p>;
    const can = teamPermissions(team.my_role, detail.settings);
    const inTeam = new Set(detail.roster.map((row) => row.user_id));
    const candidates = detail.people.filter((person) => !inTeam.has(person.user_id));

    return (
        <section className="ia-teams__card" aria-label={`${team.name} team settings`} data-testid="team-detail">
            <div className="ia-teams__head">
                <h2>{team.name}</h2>
                <span className="ia-teams__hint">You are: {TEAM_ROLE_WORDS[team.my_role]}</span>
            </div>
            {team.my_role === 'owner' && <span className="ia-teams__hint">You manage this team because a Tenant Admin made you its Team Owner.</span>}

            <h3>Linked Teams channels</h3>
            <p className="ia-teams__notice" data-testid="team-channels-later">Linking Teams channels to this team arrives with the Teams mirror. Only channels you can open in Teams yourself will appear.</p>

            <div className="ia-teams__head">
                <h3>Team members</h3>
            </div>
            <ul className="ia-teams__list" aria-label="Team members">
                {detail.roster.map((row) => (
                    <li key={row.id} className="ia-teams__line" data-member-row={row.user_id}>
                        <span className="ia-teams__line-label">{row.full_name}</span>
                        {row.team_role === 'owner' && <span className="ia-teams__hint">Team Owner (set by Tenant Admin)</span>}
                        {row.team_role !== 'owner' && (can.canManageAdmins ? (
                            <PillGroup options={[['admin', 'Team Admin'], ['member', 'Member']]} value={row.team_role} disabled={busy}
                                onChange={(role) => { if (role !== row.team_role) run(() => setTeamRole(client, row.id, role)); }} label={`Role for ${row.full_name}`} />
                        ) : <span className="ia-teams__hint">{TEAM_ROLE_WORDS[row.team_role]}</span>)}
                        {row.team_role !== 'owner' && (row.team_role === 'admin' ? can.canManageAdmins : can.canRemoveMembers) && (
                            <Button3D disabled={busy} onClick={() => run(() => removeTeamMember(client, row.id))} aria-label={`Remove ${row.full_name}`}>Remove</Button3D>
                        )}
                    </li>
                ))}
            </ul>
            {can.canAddMembers && (
                <div className="ia-teams__inline">
                    <div className="ia-teams__field">
                        <label htmlFor="team-add-person">Add member</label>
                        <select id="team-add-person" value={pick} onChange={(event) => setPick(event.target.value)}>
                            <option value="">Choose a person</option>
                            {candidates.map((person) => <option key={person.user_id} value={person.user_id}>{person.full_name}</option>)}
                        </select>
                    </div>
                    <Button3D disabled={busy || !pick} onClick={() => run(async () => { await addTeamMember(client, orgId, team.team_id, pick); setPick(''); })}>Add member</Button3D>
                </div>
            )}

            {can.canChangeSettings && draft && (
                <>
                    <h3>What Team Admins may do</h3>
                    <SwitchRow label="Team Admins can add members" checked={draft.admins_can_add_members} onChange={(value) => setDraft((current) => ({ ...current, admins_can_add_members: value }))} />
                    <SwitchRow label="Team Admins can remove members" checked={draft.admins_can_remove_members} onChange={(value) => setDraft((current) => ({ ...current, admins_can_remove_members: value }))} />
                    <div className="ia-teams__actions">
                        <Button3D variant="go" disabled={busy} onClick={() => run(() => saveTeamSettings(client, orgId, team.team_id, draft))}>Save settings</Button3D>
                    </div>
                </>
            )}
            <span className="ia-teams__hint">IntelAgent never changes Teams membership, and nobody sees a channel&apos;s messages here unless they&apos;re in that channel in Teams. Every change goes to the audit log.</span>
            {error && <p role="alert" className="ia-teams__error">{error}</p>}
        </section>
    );
}

// Teams PR A (board 6): team governance inside the app (never the Admin portal). Shows only while Teams is on for this person,
// and only the teams they belong to.
export default function TeamSpacesPage({ userId, orgId, theme = 'light', client = supabaseClient }) {
    const access = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, userId, client);
    const { teamId } = useParams();
    const [teams, setTeams] = useState(null);
    const [error, setError] = useState('');
    const allowed = canUseIntegration(access);

    const load = useCallback(async () => {
        try { setTeams(await loadTeamSpaces(client)); setError(''); } catch (failure) { setError(failure?.message || 'Your teams could not be loaded.'); }
    }, [client]);
    useEffect(() => { if (allowed) load(); }, [allowed, load]);

    if (access.loading) return <div className="p-8 text-center text-[var(--ink-muted)]">Loading…</div>;
    if (!allowed) return <Navigate to="/" replace />;

    const mine = (teams || []).filter((team) => team.my_role);
    const selected = mine.find((team) => team.team_id === teamId) || (mine.length === 1 ? mine[0] : null);

    return (
        <div data-theme={theme} className="ia-set ia-team-spaces" data-testid="team-spaces" data-m6-component="TeamSpaces">
            <nav aria-label="Breadcrumb" className="ia-set__crumbs">
                <ol>
                    <li className="ia-set__crumb-item">
                        {selected ? <Link to={TEAM_SPACES_PATH} className="ia-btn3d ia-btn3d--pill ia-set__crumb">My teams</Link> : <span aria-current="page" className="ia-set__crumb-current">My teams</span>}
                        {selected && <span aria-hidden="true" className="ia-set__crumb-sep">›</span>}
                    </li>
                    {selected && <li className="ia-set__crumb-item"><span aria-current="page" className="ia-set__crumb-current">{selected.name} › Team settings</span></li>}
                </ol>
            </nav>
            <header className="ia-set__head">
                <div className="flex items-center gap-2">
                    <h2 className="ia-set__title">My teams</h2>
                    <HoverHelp helpId="integrations.teams.team-settings" content={HELP} userId={userId} />
                </div>
                <p className="ia-set__subtitle">Your IntelAgent teams, who is in them, and who runs them.</p>
            </header>
            {error && <p role="alert" className="ia-teams__error">{error}</p>}
            {!teams && !error && <p>Loading…</p>}
            {teams && mine.length === 0 && <p className="ia-teams__notice" data-testid="team-spaces-empty">You are not in any IntelAgent team yet. A Team Owner can add you.</p>}
            <div className="ia-teams">
                {mine.length > 1 && (
                    <ul className="ia-set__grid" aria-label="Your teams">
                        {mine.map((team) => (
                            <li key={team.team_id} className="ia-set__cell">
                                <Link to={`${TEAM_SPACES_PATH}/${team.team_id}`} className="ia-set__tile ia-depth-card" style={{ boxShadow: 'var(--ia-3d-card-depth)' }} aria-current={selected?.team_id === team.team_id ? 'page' : undefined}>
                                    <span aria-hidden="true" className="ia-set__tile-icon">👥</span>
                                    <span className="ia-set__tile-text">
                                        <span className="ia-set__tile-title">{team.name}</span>
                                        <span className="ia-set__tile-blurb">{TEAM_ROLE_WORDS[team.my_role]} · {team.member_count} {team.member_count === 1 ? 'person' : 'people'}</span>
                                    </span>
                                    <span aria-hidden="true" className="ia-set__tile-chevron">›</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
                {selected && <TeamDetail key={selected.team_id} team={selected} orgId={orgId} client={client} onChanged={load} />}
            </div>
        </div>
    );
}
